import { useState, useEffect } from "react";
import { Wind, AlertTriangle, Droplets, Calendar, Clock, TrendingUp, MapPin } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import SeismicAlert from "./SeismicAlert";
import WindAlert from "./WindAlert";
import StaticSatelliteMap from "./StaticSatelliteMap";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AlertsStatsPanel = ({ sensors }) => {
  const [consumptionData, setConsumptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConsumption = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/irrigation/consumption/summary`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setConsumptionData(response.data);
        }
      } catch (error) {
        console.error('Error fetching consumption:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConsumption();
    const interval = setInterval(fetchConsumption, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatVolume = (m3) => {
    if (!m3) return '0 m³';
    if (m3 >= 1000) return `${(m3 / 1000).toFixed(1)}K m³`;
    return `${Math.round(m3)} m³`;
  };
  
  // Get status color and label
  const getStatusStyle = (status) => {
    switch (status) {
      case 'overflow':
        return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', label: 'DESBORDE' };
      case 'warning':
        return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', label: 'ALERTA' };
      case 'normal':
        return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'OK' };
      default:
        return { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', label: status?.toUpperCase() || 'N/A' };
    }
  };

  return (
    <Card className="bg-gradient-to-b from-[#050810] to-[#0a0f18] border border-blue-900/30 h-full flex flex-col overflow-hidden rounded-lg">
      <Tabs defaultValue="alerts" className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-3 bg-zinc-900/50 h-6 sm:h-7 rounded-none border-b border-blue-900/20">
          <TabsTrigger value="alerts" data-testid="tab-alerts" className="text-[9px] sm:text-[10px] data-[state=active]:bg-blue-800/50 data-[state=active]:text-blue-300 rounded-none h-5 sm:h-6">
            Alertas
          </TabsTrigger>
          <TabsTrigger value="consumption" data-testid="tab-consumption" className="text-[9px] sm:text-[10px] data-[state=active]:bg-blue-800/50 data-[state=active]:text-blue-300 rounded-none h-5 sm:h-6">
            Consumo
          </TabsTrigger>
          <TabsTrigger value="map" data-testid="tab-map" className="text-[9px] sm:text-[10px] data-[state=active]:bg-blue-800/50 data-[state=active]:text-blue-300 rounded-none h-5 sm:h-6">
            Mapa
          </TabsTrigger>
        </TabsList>

        {/* ALERTS TAB */}
        <TabsContent value="alerts" className="flex-1 p-1.5 sm:p-2 space-y-1.5 sm:space-y-2 mt-0">
          {/* Seismic and Wind Alerts */}
          <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
            <SeismicAlert />
            <WindAlert />
          </div>
          
          {/* Current Status Summary - Compacto */}
          <div className="bg-[#0a1018] rounded-lg p-1.5 sm:p-2 space-y-0.5 sm:space-y-1">
            <p className="text-[8px] sm:text-[10px] text-blue-300/70 uppercase tracking-wider font-bold mb-0.5 sm:mb-1">Estado Actual Presas</p>
            {sensors.filter(s => ['PARRALILLO', 'SIBERIO', 'CAIDERO'].includes(s.name)).map(sensor => {
              const statusStyle = getStatusStyle(sensor.status);
              return (
                <div key={sensor.id} className={`flex items-center justify-between py-1 sm:py-1.5 px-1.5 sm:px-2 rounded ${statusStyle.bg}`}>
                  <span className="text-[10px] sm:text-xs text-zinc-200 font-semibold">{sensor.name}</span>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="text-sm sm:text-base font-mono font-bold text-cyan-300">{sensor.current_level?.toFixed(2)}<span className="text-[8px] sm:text-[10px] text-cyan-300/60">m</span></span>
                    <span className={`text-[8px] sm:text-[9px] px-1 sm:px-1.5 py-0.5 rounded font-bold ${statusStyle.bg} ${statusStyle.text}`}>
                      {statusStyle.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* CONSUMPTION TAB */}
        <TabsContent value="consumption" className="flex-1 p-1.5 sm:p-2 space-y-1 sm:space-y-1.5 mt-0">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-zinc-500 text-[10px] sm:text-xs">Cargando...</span>
            </div>
          ) : (
            <>
              {/* Today */}
              <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-1.5 sm:p-2">
                <div className="flex items-center gap-1 mb-0.5 sm:mb-1">
                  <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-400" />
                  <span className="text-[9px] sm:text-[10px] text-blue-300 uppercase font-bold">Hoy</span>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <div className="text-center bg-[#0a1018] rounded py-1 sm:py-1.5">
                    <span className="text-sm sm:text-base font-mono font-bold text-amber-400">
                      {consumptionData?.today?.hours_water?.toFixed(1) || '0'}
                    </span>
                    <p className="text-[8px] sm:text-[9px] text-zinc-500">Horas</p>
                  </div>
                  <div className="text-center bg-[#0a1018] rounded py-1 sm:py-1.5">
                    <span className="text-sm sm:text-base font-mono font-bold text-cyan-400">
                      {formatVolume(consumptionData?.today?.volume_m3)}
                    </span>
                    <p className="text-[8px] sm:text-[9px] text-zinc-500">m³</p>
                  </div>
                  <div className="text-center bg-[#0a1018] rounded py-1 sm:py-1.5">
                    <span className="text-sm sm:text-base font-mono font-bold text-green-400">
                      {consumptionData?.today?.operations || 0}
                    </span>
                    <p className="text-[8px] sm:text-[9px] text-zinc-500">Ops</p>
                  </div>
                </div>
              </div>

              {/* Week */}
              <div className="bg-[#0a1018] rounded-lg p-1.5 sm:p-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-zinc-500" />
                    <span className="text-[8px] sm:text-[9px] text-zinc-400 uppercase font-bold">Semana</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-[10px] sm:text-xs font-mono font-bold text-amber-400/80">
                      {consumptionData?.week?.hours_water?.toFixed(1) || '0'}h
                    </span>
                    <span className="text-[10px] sm:text-xs font-mono font-bold text-cyan-400/80">
                      {formatVolume(consumptionData?.week?.volume_m3)}
                    </span>
                    <span className="text-[10px] sm:text-xs font-mono font-bold text-green-400/80">
                      {consumptionData?.week?.operations || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Month */}
              <div className="bg-[#0a1018] rounded-lg p-1.5 sm:p-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    <TrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-zinc-500" />
                    <span className="text-[8px] sm:text-[9px] text-zinc-400 uppercase font-bold">Mes</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-[10px] sm:text-xs font-mono font-bold text-amber-400/80">
                      {consumptionData?.month?.hours_water?.toFixed(1) || '0'}h
                    </span>
                    <span className="text-[10px] sm:text-xs font-mono font-bold text-cyan-400/80">
                      {formatVolume(consumptionData?.month?.volume_m3)}
                    </span>
                    <span className="text-[10px] sm:text-xs font-mono font-bold text-green-400/80">
                      {consumptionData?.month?.operations || 0}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* MAP TAB */}
        <TabsContent value="map" className="flex-1 mt-0 p-0">
          <StaticSatelliteMap sensors={sensors} />
        </TabsContent>
      </Tabs>
    </Card>
  );
};

export default AlertsStatsPanel;
